import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { fetchHelper } from '../helpers/fetchHelper'

export interface VerificationRequiredProps {
  emailVerified: boolean
  mobileVerified: boolean
  email: string
  mobile: string
  onUpdateVerification: () => void
}

const VerificationRequired = (props: VerificationRequiredProps) => {
  const [step, setStep] = useState(1); // 1: initial warning, 2: verification sent, 3: fully verified
  const [showResend, setShowResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const resendInterval = useRef<NodeJS.Timeout | null>(null);
  const reverifyInterval = useRef<NodeJS.Timeout | null>(null);

  const handleVerifyNowClick = () => {
    setStep(2)
    resendVerification()
  };

  const handleClose = () => {
    window.location.reload()
  };

  const resendVerification = async () => {
    const response = await fetchHelper.postJson('api/Dashboard/ResendVerificationLinks', {})

    if (response.status !== 200) {
      console.error('Error resending verification links:', response.statusText)
    }
  }

  // setup reverify interval to check if the user has verified their email or mobile number. this should be every 20 seconds
  useEffect(() => {
    if (step === 2) {
      reverifyInterval.current = setInterval(() => {
        props.onUpdateVerification()
      }, 20000)
    }

    return () => {
      if (reverifyInterval.current) {
        clearInterval(reverifyInterval.current);
      }
    }
  }, [step, props])

  useEffect(() => {
    if (step === 2) {
      setShowResend(false);
      setResendCountdown(60);

      resendInterval.current = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            if (resendInterval.current) {
              clearInterval(resendInterval.current)
            }
            setShowResend(true)
            return 0
          }
          return prev - 1
        });
      }, 1000)
    }

    return () => {
      if (resendInterval.current) {
        clearInterval(resendInterval.current);
      }
    }
  }, [step])

  return (
    <div className="verify-contact-details">
      {/* Step 1: Needs verification */}
      {step === 1 && (
        <div>
          <h4 className="text-500 text-lightest">All calls are blocked</h4>
          <p>
            As you haven’t verified your email (<b>{props.email}</b>) and mobile number (<b>{props.mobile}</b>), all calls are currently blocked. Please verify your contact details now.
          </p>
          <p className="text-center">
            <Link to="#" onClick={handleVerifyNowClick} className="btn btn-lightest mt-20">Verify Now</Link>
          </p>
        </div>
      )}

      {/* Step 2: Verification sent */}
      {step === 2 &&
        <>
          <div>
            <h4 className="text-500 text-lightest">Verification link has been sent</h4>
            <p className="mt-20">
              A verification email has been sent to <b>{props.email}</b> and an SMS to <b>{props.mobile}</b>. Both should arrive immediately. If you don’t receive them, please check your spam or junk folder. Once you click the verification link in the email and SMS, your account will be fully verified and you can make and receive calls.
            </p>
            <div className="verification-steps">
              <h6 className="text-500 mt-30">Real-time verification check</h6>
              <p className="b-1">
                <span className="w-50 d-inline-block">
                  Checking if you verified your mobile number
                </span>
                <span className="text-end float-end w-50">
                  {props.mobileVerified ?
                    <span className="text-600 text-success"><span className="icon icon-checkmark4"></span> Verified</span> :
                    <span className="text-600 text-danger">
                      <img src={'images/icons/loading.svg'} width="20" height="20" />
                      Not Verified
                    </span>
                  }
                </span>
              </p>
              <p className="b-1">
                <span className="w-50 d-inline-block">
                  Checking if you verified your email address
                </span>
                <span className="text-end float-end w-50">
                  {props.emailVerified ?
                    <span className="text-600 text-success"><span className="icon icon-checkmark4"></span> Verified</span> :
                    <span className="text-600 text-danger">
                      <img src={'images/icons/loading.svg'} width="20" height="20" />
                      Not Verified
                    </span>
                  }                
                </span>
              </p>
            </div>
            <div className="text-center mt-30">
              <Link
                to="#"
                className={`btn btn-lightest ${!showResend ? 'disabled' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (showResend) {
                    fetchHelper.postJson('api/Dashboard/ResendVerificationLinks', {})
                    setShowResend(false);
                    setResendCountdown(60);
                  }
                }}
              >
                Resend
              </Link>
              {!showResend && (
                <p className="d-block mt-10 text-lightest text-xs">
                  You can resend in {resendCountdown} seconds...
                </p>
              )}
            </div>
          </div>
        </>
      }

      {/* Step 3: Fully verified */}
      {step === 3 && (
        <div>
          <h4 className="text-500 text-lightest">Thank you!</h4>
          <p className="mt-20">
            Your details have been verified successfully. You can now make and receive calls.
          </p>
          <p className="text-center">
            <Link to="#" onClick={handleClose} className="btn btn-lightest mt-20">Close</Link>
          </p>
        </div>
      )}
    </div>
  );
};

export default connect(
  (state) => state,
  {}
)(VerificationRequired)
